<template>
  <div class="paie-details">
    <ui-wad
      v-if="fetchState.status.value === 'success'"
      class="paie-details__wrapper animate__animated animate__fadeIn ease-in-out a1"
    >
      <div class="paie-details__header">
        <details-header />
      </div>
      <layout-nav-tab-menu
        v-model="selectedTabMenu"
        :items="NAV_SECTION_OPTIONS"
        class="paie-details__tab-menu"
      />
      <div class="paie-details__body">
        <!-- Tabs -->
        <details-tab
          v-show="isSelectedTabMenu('details')"
          class="paie-details__body__details"
        />

        <retenues-tab
          v-show="isSelectedTabMenu('retenues')"
          class="paie-details__body__retenues"
        />
        <!-- Tabs -->
      </div>
    </ui-wad>

    <!-- Loading screen -->
    <div
      v-if="fetchState.status.value === 'pending'"
      class="paie-details__loading animate__animated animate__fadeIn ease-in-out a1 d1"
    >
      <ui-spinner
        :type="UI.SPINNER_TYPE.BORDER"
        :size="UI.SIZE.MEDIUM"
      />
      <div class="paie-details__loading__text animate__animated animate__fadeIn ease-in-out a2 d2">
        Chargement des informations de la paie...
      </div>
    </div>
    <!-- Loading screen -->

    <!-- Error screen -->
    <div
      v-if="fetchState.status.value === 'error'"
      class="paie-details__error animate__animated animate__fadeIn ease-in-out a1"
    >
      <div class="paie-details__error__text">
        Erreur lors de la récupération des informations de la paie
      </div>
      <ui-well
        :variant="UI.VARIANT.DANGER"
        :size="UI.SIZE.SMALL"
      >
        {{ errorMessage }}
      </ui-well>
      <ui-link-button-meek
        :size="UI.SIZE.SMALL"
        :route="parentRoute"
        bulky
      >
        Retour à la liste des paies
      </ui-link-button-meek>
    </div>
    <!-- Error screen -->
  </div>
</template>

<script lang="ts">
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useCompanyOptionalNavigationContext } from '~/composables/layout/navigation'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useStore_paies_details } from '~/stores/paies/details'
import { useStore_paies_rubriques } from '~/stores/paies/rubriques'
import type { PaieDetails } from '~/stores/paies'
import { join, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getPaiesListRoute } from '~/stores/paies/routes'
import DetailsHeader from '~/components/paies-bis/details/details/Header.vue'
import DetailsTab from '~/components/paies-bis/details/details/Tab.vue'
import RetenuesTab from '~/components/paies-bis/details/retenues/Tab.vue'
import { useStore_paies_draft } from '~/stores/paies/draft'
import { usePaieDetailsSocketsEvents } from '~/composables/paies/usePaieDetailsSocketEvents'

const NAV_SECTION_OPTIONS = [
  {
    id: 'details',
    text: 'Détails',
  },
  {
    id: 'retenues',
    text: 'Retenues',
  },
]

export default defineNuxtComponent({
  components: {
    DetailsHeader,
    DetailsTab,
    RetenuesTab,
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.PAIES,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const store_paies_details = useStore_paies_details()
    const store_paies_rubriques = useStore_paies_rubriques()
    const store_paies_draft = useStore_paies_draft()

    usePaieDetailsSocketsEvents()

    return {
      ...useCompanyOptionalNavigationContext(),
      fetchState,
      fetchWrapper,
      store_paies_details,
      store_paies_rubriques,
      store_paies_draft,
    }
  },
  data() {
    return {
      selectedTabMenu: 'details',
      NAV_SECTION_OPTIONS,
      paie: null as unknown as PaieDetails,
      errorMessage: '',
    }
  },
  computed:{
    parentRoute() {
      const currentLevel = this.fromCurrentLevel()
      return getPaiesListRoute(
        currentLevel.structure.id,
        currentLevel.kind === 'COMPANY' ? currentLevel.company.code_societe : '',
      )
    },
    pageTitle() {
      const currentLevel = this.fromCurrentLevel()

      switch (currentLevel.kind) {
        case 'COMPANY':
          return `Paies — ${currentLevel.company.raison_sociale} — `
        case 'STRUCTURE':
          return `Paies — ${currentLevel.structure.raison} — `
        default:
          return 'Paies — '
      }
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  beforeUnmount() {
    this.store_paies_rubriques.$reset()
    this.store_paies_draft.$reset()
  },
  methods: {
    isSelectedTabMenu(id: string) {
      return this.selectedTabMenu === id
    },
    fetch() {
      const {
        structure: { uuidData },
      } = this.fromCurrentLevel()

      const paieId = this.$route.params.id as string
      const societeId = this.$route.params.societe_id as string

      return Promise.all([
        this.store_paies_details.read({ uuidData, societeId, paieId }),
        this.store_paies_details.rubriques({ uuidData, societeId })
      ]).then(([paie, { rubriques: remoteRubriques }]) => {
        this.paie = paie
        this.store_paies_rubriques.init(paie.rubriques, remoteRubriques)
        this.store_paies_draft.init(paie)

        setPageTitle(this.pageTitle, paie)
      })
      .catch(tapCatch((err) => {
        this.errorMessage = toString(err)
      }))
    }
  },
})

function setPageTitle(
  title: string,
  paie :PaieDetails,
) {
  document.title = title + join(' ', [
    `${paie.salarie.matricule} -`,
    `n°${paie.numero_paie}`,
  ])
}
</script>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import '~/assets/sass/modules/layout/members'
@import ~/assets/sass/modules/paie/variables

//empirical to align header
$offset-x: space-x(2)

.paie-details
  height: 100%

  &__wrapper
    background-color: $background-tertiary
    display: flex
    flex-direction: column
    height: 100%

  &__header
    padding: ($layout-spacer-y * .5) ($layout-spacer-x * .75) ($layout-spacer-y * .5)
    padding-bottom: 0
    background-color: $subheader-bg

  &__tab-menu
    padding-left: $offset-x
    padding-top: ($layout-spacer-y * .5)

  &__body
    background-color: $background-tertiary
    display: flex
    flex-direction: column
    flex-grow: 1
    padding-bottom: ($layout-spacer-y * .75)

    &__retenues
      height: 100%

    &__details
      height: 100%

  &__loading,
  &__error
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    height: 100%
    background-color: $background-tertiary
    padding: $layout-spacer-y $layout-spacer-x
    gap: space-x(2)

    &__text
      font-size: 110%
      color: $gray-medium
      font-weight: $font-weight-light
      margin-top: space-x(2)
</style>
